import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiLoader, FiShield, FiLock } from 'react-icons/fi';
import brandLogoImg from '../assets/logo.png';

function PinDot({ filled }) {
  return (
    <div
      className={`h-4 w-4 rounded-full border-2 transition-all duration-200 ${
        filled ? 'bg-gray-900 border-gray-900 scale-110' : 'border-gray-300 bg-white'
      }`}
    />
  );
}

function Login() {
  const brandLogoSrc = brandLogoImg || (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/logo.png` : '/logo.png');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'security' ? 'security' : 'regular';
  const [loginMode, setLoginMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [step, setStep] = useState('credentials'); // 'credentials' | 'pin'
  const [pendingLocation, setPendingLocation] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState('pending');
  const { user, loading: authLoading, login, securityLogin } = useAuth();
  const navigate = useNavigate();

  // If already authenticated, redirect immediately so pressing back doesn't show login
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const switchMode = (mode) => {
    setLoginMode(mode);
    setError('');
    setStep('credentials');
    setPin('');
    setSearchParams(mode === 'security' ? { mode: 'security' } : {});
  };

  const captureBrowserLocation = async () => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    const capturedAt = new Date().toISOString();

    const getPosition = (options) =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          return reject(new Error('unsupported'));
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });

    // Stage 1: Quick high accuracy (GPS).
    // Short 3.5s timeout so phones do not stall indoors; accept cached location up to 5 minutes.
    try {
      const position = await getPosition({
        enableHighAccuracy: true,
        timeout: 3500,
        maximumAge: 300000
      });

      if (position?.coords) {
        setLocationStatus('granted');
        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          browser_timezone: browserTimezone,
          captured_at: capturedAt,
          source: 'browser_gps'
        };
      }
    } catch (gpsErr) {
      // Continue immediately to cellular/Wi-Fi positioning if GPS timed out or satellite signal unavailable
    }

    // Stage 2: Fast Wi-Fi / Cellular low-accuracy positioning.
    // Instantaneous on mobile devices indoors; accept cached location up to 10 minutes.
    try {
      const position = await getPosition({
        enableHighAccuracy: false,
        timeout: 4000,
        maximumAge: 600000
      });

      if (position?.coords) {
        setLocationStatus('granted');
        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          browser_timezone: browserTimezone,
          captured_at: capturedAt,
          source: 'browser_network'
        };
      }
    } catch (networkErr) {
      // If browser permission is blocked, denied, or restricted in mobile WebView
    }

    // Stage 3: Seamless IP/Network Fallback.
    // Never block staff or make them retry repeatedly; pass fallback payload so backend
    // logs location via server-side IP lookup and permits login smoothly.
    setLocationStatus('network_fallback');
    return {
      latitude: null,
      longitude: null,
      accuracy: null,
      browser_timezone: browserTimezone,
      captured_at: capturedAt,
      source: 'ip_fallback',
      reason: 'gps_cellular_unavailable'
    };
  };

  const ensureLocationPermission = async () => {
    return await captureBrowserLocation();
  };

  // Step 1: validate credentials
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const clientLocation = await ensureLocationPermission();

      if (loginMode === 'security') {
        await securityLogin(email, password, { client_location: clientLocation });
        navigate('/security/login-history', { replace: true });
        return;
      }

      // Call login WITHOUT a PIN to check if one is required
      const result = await login(email, password, { client_location: clientLocation });

      if (result?.requires_hod_pin) {
        // Store location so the PIN step can reuse it
        setPendingLocation(clientLocation);
        setStep('pin');
        return;
      }

      // Normal login success (admin / manager / operator)
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: submit PIN for HOD users
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) { setError('Please enter your full 4-digit PIN.'); return; }
    setError('');
    setLoading(true);

    try {
      const clientLocation = pendingLocation || await ensureLocationPermission();
      await login(email, password, { client_location: clientLocation, hod_pin: pin });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Login failed');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Numpad key handler for PIN input
  const handlePinKey = (key) => {
    if (key === 'del') { setPin(prev => prev.slice(0, -1)); return; }
    if (pin.length < 4) setPin(prev => prev + key);
  };

  return (
    <div className="min-h-screen bg-[#f4f5fb] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center">
        <div className="grid w-full gap-6 lg:grid-cols-2">
          {/* Left panel */}
          <div className="fleet-card hidden p-10 lg:flex lg:flex-col lg:justify-between">
            <div className="flex items-center gap-3">
              <img
                src={brandLogoSrc}
                alt="ElyChat Logo"
                className="h-12 w-auto max-w-[200px] object-contain"
                onError={(e) => {
                  if (e.currentTarget.src !== 'https://elyspr.com/logo-1.png') {
                    e.currentTarget.src = 'https://elyspr.com/logo-1.png';
                  }
                }}
              />
            </div>
            <div className="space-y-4">
              <h1 className="text-5xl font-bold tracking-tight text-gray-900">ElyChat</h1>
              <p className="max-w-md text-base leading-relaxed text-gray-600">
                Concierge operations, guest communication, and department workflows in one intelligent workspace.
              </p>
            </div>
            <div className="text-xs text-gray-500">Secure access for admin, manager, and operations teams.</div>
          </div>

          {/* Right panel */}
          <div className="fleet-card p-6 sm:p-8 lg:p-10">
            <div className="mb-8 text-center lg:text-left">
              <div className="mb-6 flex justify-center lg:hidden">
                <img
                  src={brandLogoSrc}
                  alt="ElyChat Logo"
                  className="h-14 w-auto max-w-[220px] object-contain"
                  onError={(e) => {
                    if (e.currentTarget.src !== 'https://elyspr.com/logo-1.png') {
                      e.currentTarget.src = 'https://elyspr.com/logo-1.png';
                    }
                  }}
                />
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                {step === 'pin' ? 'Enter Your HOD PIN' : 'Welcome Back'}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {step === 'pin'
                  ? 'Your credentials are verified. Enter your personal 4-digit security PIN.'
                  : loginMode === 'security'
                  ? 'Security team sign-in with authorized credentials'
                  : 'Sign in to continue to your dashboard'}
              </p>
            </div>

            {/* Mode switcher removed as requested. Accessible via URL ?mode=security */}

            {/* ── CREDENTIALS STEP ── */}
            {step === 'credentials' && (
              <form onSubmit={handleCredentialsSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Email Address</label>
                  <input id="email" type="email" placeholder="Enter your email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required className="fleet-input h-12" disabled={loading} />
                </div>
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Password</label>
                  <input id="password" type="password" placeholder="Enter your password" value={password}
                    onChange={(e) => setPassword(e.target.value)} required className="fleet-input h-12" disabled={loading} />
                </div>

                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-medium text-red-700">{error}</p>
                  </div>
                )}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs flex items-center justify-between">
                  <span className="text-gray-600 font-medium">Location check</span>
                  {locationStatus === 'granted' ? (
                    <span className="inline-flex items-center text-emerald-600 font-semibold">
                      <span className="mr-1.5 h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                      Verified (GPS / Wi-Fi)
                    </span>
                  ) : locationStatus === 'network_fallback' ? (
                    <span className="inline-flex items-center text-blue-600 font-semibold">
                      <span className="mr-1.5 h-2 w-2 rounded-full bg-blue-500"></span>
                      Verified (Network / IP)
                    </span>
                  ) : (
                    <span className="text-gray-500">
                      Auto-verified on sign-in
                    </span>
                  )}
                </div>
                <button type="submit" className="flex h-12 w-full items-center justify-center rounded-xl bg-gray-900 text-sm font-semibold text-white transition-all hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50" disabled={loading}>
                  {loading ? <><FiLoader className="mr-2 h-4 w-4 animate-spin" />Signing in...</> : (loginMode === 'security' ? 'Login to Security Portal' : 'Login')}
                </button>
              </form>
            )}

            {/* ── PIN STEP ── */}
            {step === 'pin' && (
              <form onSubmit={handlePinSubmit} className="space-y-6">
                {/* PIN dots indicator */}
                <div className="flex justify-center gap-4 py-4">
                  {[0, 1, 2, 3].map(i => <PinDot key={i} filled={i < pin.length} />)}
                </div>

                {/* Numpad */}
                <div className="grid grid-cols-3 gap-3">
                  {['1','2','3','4','5','6','7','8','9','','0','del'].map((key, idx) => {
                    if (key === '') return <div key={idx} />;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handlePinKey(key)}
                        disabled={loading}
                        className={`h-14 rounded-xl border text-lg font-semibold transition-all active:scale-95 disabled:opacity-50 ${
                          key === 'del'
                            ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                            : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50 shadow-sm'
                        }`}
                      >
                        {key === 'del' ? '⌫' : key}
                      </button>
                    );
                  })}
                </div>

                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-medium text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button type="button" onClick={() => { setStep('credentials'); setPin(''); setError(''); }}
                    className="flex-1 h-12 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all">
                    ← Back
                  </button>
                  <button type="submit" disabled={loading || pin.length !== 4}
                    className="flex-1 flex h-12 items-center justify-center rounded-xl bg-gray-900 text-sm font-semibold text-white transition-all hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50">
                    {loading ? <><FiLoader className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : <><FiShield className="mr-2 h-4 w-4" />Confirm PIN</>}
                  </button>
                </div>

                <p className="text-center text-xs text-gray-500">
                  <FiLock className="inline mr-1 h-3 w-3" />
                  Your PIN is unique to you and is used for identity tracking
                </p>
              </form>
            )}

            {step === 'credentials' && (
              <div className="mt-6 border-t border-gray-100 pt-5">
                <p className="text-center text-sm text-gray-600">
                  Don&apos;t have an account?{' '}
                  <button type="button" onClick={() => alert('Signup functionality coming soon. Contact support for access.')}
                    className="font-semibold text-gray-900 underline underline-offset-4 transition-colors hover:text-gray-700">
                    Create one now
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
